import { supabase } from '../lib/supabase';


function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const webinarService = {
  // Get all webinars with filters
  async getWebinars(filters = {}) {
    try {
      let query = supabase?.from('webinar_sessions')?.select(`
          *,
          expert:expert_profiles(*)
        `);

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.topic) {
        query = query?.eq('topic', filters?.topic);
      }

      if (filters?.expertId) {
        query = query?.eq('expert_id', filters?.expertId);
      }

      query = query?.order('scheduled_at', { ascending: filters?.status === 'completed' ? false : true });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching webinars:', error);
      throw error;
    }
  },

  // Get single webinar by ID
  async getWebinarById(id) {
    try {
      const { data, error } = await supabase?.from('webinar_sessions')?.select(`
          *,
          expert:expert_profiles(*)
        `)?.eq('id', id)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data;
    } catch (error) {
      console.error('Error fetching webinar:', error);
      throw error;
    }
  },

  // Get all experts
  async getExperts() {
    try {
      const { data, error } = await supabase?.from('expert_profiles')?.select('*')?.order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching experts:', error);
      throw error;
    }
  },

  // Get expert by ID
  async getExpertById(id) {
    try {
      const { data, error } = await supabase?.from('expert_profiles')?.select('*')?.eq('id', id)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data;
    } catch (error) {
      console.error('Error fetching expert:', error);
      throw error;
    }
  },

  // Register for webinar
  async registerForWebinar(webinarId, studentId) {
    try {
      // Check if a registration already exists (including cancelled ones)
      const { data: existing, error: checkError } = await supabase
        ?.from('webinar_registrations')
        ?.select('id, status')
        ?.eq('webinar_id', webinarId)
        ?.eq('student_id', studentId)
        ?.maybeSingle();

      if (checkError && !isSchemaError(checkError)) {
        console.log('Check error:', checkError?.message);
      }

      let data;
      if (existing) {
        if (existing?.status === 'registered') {
          throw new Error('You are already registered for this webinar');
        }
        // Re-register (update cancelled/attended back to registered)
        const { data: updated, error: updateError } = await supabase
          ?.from('webinar_registrations')
          ?.update({ status: 'registered', registered_at: new Date()?.toISOString() })
          ?.eq('id', existing?.id)
          ?.select()
          ?.single();

        if (updateError) {
          if (isSchemaError(updateError)) {
            console.error('Schema error:', updateError?.message);
            throw updateError;
          }
          throw new Error('Failed to register for webinar');
        }
        data = updated;
      } else {
        // New registration
        const { data: inserted, error } = await supabase
          ?.from('webinar_registrations')
          ?.insert({
            webinar_id: webinarId,
            student_id: studentId,
            status: 'registered'
          })
          ?.select()
          ?.single();

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error?.message);
            throw error;
          } else if (error?.code === '23505') {
            // Unique constraint - race condition, try update
            const { data: upserted, error: upsertError } = await supabase
              ?.from('webinar_registrations')
              ?.update({ status: 'registered', registered_at: new Date()?.toISOString() })
              ?.eq('webinar_id', webinarId)
              ?.eq('student_id', studentId)
              ?.select()
              ?.single();
            if (upsertError) throw new Error('Failed to register for webinar');
            data = upserted;
          } else {
            console.log('Registration error:', error?.message);
            throw new Error('Failed to register for webinar');
          }
        } else {
          data = inserted;
        }
      }

      // Update participant count
      await this.updateParticipantCount(webinarId);

      return data;
    } catch (error) {
      console.error('Error registering for webinar:', error);
      throw error;
    }
  },

  // Cancel registration
  async cancelRegistration(webinarId, studentId) {
    try {
      const { error } = await supabase?.from('webinar_registrations')?.update({ status: 'cancelled' })?.eq('webinar_id', webinarId)?.eq('student_id', studentId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Cancellation error:', error?.message);
          throw new Error('Failed to cancel registration');
        }
      }

      // Update participant count
      await this.updateParticipantCount(webinarId);

      return true;
    } catch (error) {
      console.error('Error cancelling registration:', error);
      throw error;
    }
  },

  // Get user registrations
  async getUserRegistrations(studentId) {
    try {
      const { data, error } = await supabase?.from('webinar_registrations')?.select(`
          *,
          webinar:webinar_sessions(
            *,
            expert:expert_profiles(*)
          )
        `)?.eq('student_id', studentId)?.order('registered_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user registrations:', error);
      throw error;
    }
  },

  // Check if user is registered
  async isUserRegistered(webinarId, studentId) {
    try {
      const { data, error } = await supabase?.from('webinar_registrations')?.select('id, status')?.eq('webinar_id', webinarId)?.eq('student_id', studentId)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }

      return data && data?.status === 'registered';
    } catch (error) {
      console.error('Error checking registration:', error);
      return false;
    }
  },

  // Update participant count
  async updateParticipantCount(webinarId) {
    try {
      const { count, error: countError } = await supabase?.from('webinar_registrations')?.select('*', { count: 'exact', head: true })?.eq('webinar_id', webinarId)?.eq('status', 'registered');

      if (countError) {
        console.log('Count error:', countError?.message);
        return;
      }

      const { error: updateError } = await supabase?.from('webinar_sessions')?.update({ participant_count: count || 0 })?.eq('id', webinarId);

      if (updateError) {
        console.log('Update error:', updateError?.message);
      }
    } catch (error) {
      console.error('Error updating participant count:', error);
    }
  },

  // Admin: Create webinar
  async createWebinar(webinarData) {
    try {
      const { data, error } = await supabase?.from('webinar_sessions')?.insert(webinarData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Creation error:', error?.message);
          throw new Error('Failed to create webinar');
        }
      }

      return data;
    } catch (error) {
      console.error('Error creating webinar:', error);
      throw error;
    }
  },

  // Admin: Update webinar
  async updateWebinar(id, updates) {
    try {
      const { data, error } = await supabase?.from('webinar_sessions')?.update(updates)?.eq('id', id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Update error:', error?.message);
          throw new Error('Failed to update webinar');
        }
      }

      return data;
    } catch (error) {
      console.error('Error updating webinar:', error);
      throw error;
    }
  },

  // Admin: Delete webinar
  async deleteWebinar(id) {
    try {
      const { error } = await supabase?.from('webinar_sessions')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Deletion error:', error?.message);
          throw new Error('Failed to delete webinar');
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting webinar:', error);
      throw error;
    }
  },

  // Admin: Create expert
  async createExpert(expertData) {
    try {
      const { data, error } = await supabase?.from('expert_profiles')?.insert(expertData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Creation error:', error?.message);
          throw new Error('Failed to create expert profile');
        }
      }

      return data;
    } catch (error) {
      console.error('Error creating expert:', error);
      throw error;
    }
  },

  // Admin: Update expert
  async updateExpert(id, updates) {
    try {
      const { data, error } = await supabase?.from('expert_profiles')?.update(updates)?.eq('id', id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Update error:', error?.message);
          throw new Error('Failed to update expert profile');
        }
      }

      return data;
    } catch (error) {
      console.error('Error updating expert:', error);
      throw error;
    }
  },

  // Admin: Delete expert
  async deleteExpert(id) {
    try {
      const { error } = await supabase?.from('expert_profiles')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Deletion error:', error?.message);
          throw new Error('Failed to delete expert profile');
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting expert:', error);
      throw error;
    }
  },

  // Get webinar registrations (admin)
  async getWebinarRegistrations(webinarId) {
    try {
      const { data, error } = await supabase?.from('webinar_registrations')?.select(`
          *,
          student:users(id, name, email)
        `)?.eq('webinar_id', webinarId)?.order('registered_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching registrations:', error);
      throw error;
    }
  }
};

export default webinarService;